import { useTheme } from '@mui/material';
import { editor } from 'monaco-editor';
import { useRef, useState } from 'react';

import { TCommonCodeEditorOptionsProps } from '@/shared/hooks/use-code-editor-settings/use-code-editor-settings.types';
import { useMonacoTheme } from '@/shared/hooks/use-monaco-theme/use-monaco-theme';

export const LINE_HEIGHT = 18;

export const useCodeEditorSettings = <T extends editor.IEditor>(
  language: string = 'st',
  transparentBackground?: boolean,
) => {
  const theme = useTheme();
  const { currentThemeId, registerThemes } = useMonacoTheme(
    transparentBackground,
  );
  const editorRef = useRef<T | null>(null);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const getCommonEditorOptions = (
    options?: TCommonCodeEditorOptionsProps,
  ): editor.IStandaloneEditorConstructionOptions => {
    const fontSize = options?.fontSize ?? 14;
    const scrollEnabled = options?.scrollEnabled ?? true;
    const editable = options?.editable ?? false;

    return {
      fontSize,
      fontFamily: 'Courier New',
      fontWeight: '700',
      letterSpacing: 0,
      lineHeight: LINE_HEIGHT,
      minimap: {
        enabled: false,
      },
      readOnly: !editable,
      automaticLayout: true,
      scrollbar: {
        vertical: 'visible',
        handleMouseWheel: scrollEnabled,
        horizontalHasArrows: true,
      },
      scrollBeyondLastLine: false,
    };
  };

  const handleBeforeMount = (monaco: any) => {
    if (monaco) {
      monaco.editor.defineTheme(`${language}-language`, {
        base: theme.palette.mode === 'dark' ? 'vs-dark' : 'vs',
        inherit: true,
        rules: [],
        colors: {},
      });
    }
  };

  const handleEditorMount = (editor: T, monaco: any) => {
    if (monaco && monaco.editor) {
      editorRef.current = editor;
      registerThemes(monaco.editor);

      if ('onDidFocusEditorText' in editor && 'onDidBlurEditorText' in editor) {
        const standaloneEditor =
          editor as unknown as editor.IStandaloneCodeEditor;

        standaloneEditor.onDidFocusEditorText(() => {
          setIsFocused(true);
        });

        standaloneEditor.onDidBlurEditorText(() => {
          setIsFocused(false);
        });
      }
    }
  };

  return {
    theme,
    currentThemeId,
    editorRef,
    getCommonEditorOptions,
    handleBeforeMount,
    handleEditorMount,
    isFocused,
  };
};
